/**
 * @author      xinzhe.chai@zumper.com (Xinzhe Chai)
 * @copyright   Copyright (c) 2021, Zumper
 */

import React, { ReactNode, MouseEvent } from 'react'
import { Link, LinkProps } from 'react-router-dom'

import { ButtonSize, InjectEmotionCSSType } from '../../constants'
import { getSizeStyle } from '../../helpers'

import * as styles from './styles'

export interface LinkButtonProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Emotion CSS applied to button */
  buttonStyle?: InjectEmotionCSSType
  /** Child component */
  children?: ReactNode | ReactNode[]
  /** For testing purposes */
  dataTestId?: string
  /** Whether button responds to click */
  isDisabled?: boolean
  /** `href` link to open */
  href?: string
  /** Icon inside the button */
  icon?: ReactNode
  /** Emotion CSS applied to icon */
  iconStyle?: InjectEmotionCSSType
  /** Emotion CSS applied to Link */
  linkStyle?: InjectEmotionCSSType
  /** onClick event for button */
  onClick?: (e?: MouseEvent<HTMLElement>) => void
  /** Size of the button */
  size?: ButtonSize
  /** Tab Index of button */
  tabIndex?: number
  /** `target` prop in `<a>` */
  target?: string
  /** Emotion CSS applied to text */
  textStyle?: InjectEmotionCSSType
  /** React router link */
  to?: LinkProps['to']
  /** Whether component uses the button style */
  useButtonStyles?: boolean
}

/**
 * @param props
 * @deprecated Please use Button from @chakra-ui/react instead
 */
export const LinkButton = (props: LinkButtonProps) => {
  const {
    buttonStyle,
    children,
    dataTestId,
    isDisabled,
    href,
    icon,
    iconStyle,
    linkStyle,
    onClick,
    onMouseDown,
    size,
    tabIndex,
    target,
    textStyle,
    to,
    useButtonStyles,
    ...rest
  } = props

  const rel = target === '_blank' ? 'noopener noreferrer' : undefined

  const styleValue = useButtonStyles
    ? [
        styles.button,
        styles.baseButton,
        size ? getSizeStyle(size) : getSizeStyle('md'),
        buttonStyle,
        isDisabled && styles.disabled,
      ]
    : [styles.linkButton, linkStyle, isDisabled && styles.disabled]

  if (to) {
    return (
      <Link
        css={styleValue}
        data-testid={dataTestId}
        onClick={onClick}
        onMouseDown={onMouseDown}
        role="button"
        tabIndex={isDisabled ? -1 : tabIndex}
        to={to}
        {...rest}
      >
        {icon && <span css={[styles.icon, iconStyle]}>{icon}</span>}
        <span css={[styles.text, textStyle]}>{children}</span>
      </Link>
    )
  }
  return (
    <a
      css={styleValue}
      href={href ?? ''}
      onClick={onClick}
      onMouseDown={onMouseDown}
      rel={rel}
      role="button"
      tabIndex={isDisabled ? -1 : tabIndex}
      target={target}
      {...rest}
    >
      {icon && <span css={[styles.icon, iconStyle]}>{icon}</span>}
      <span css={[styles.text, textStyle]}>{children}</span>
    </a>
  )
}
